import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { RecordItem, OperationType, OperationMethod } from '../zones/BusinessMapTab';
import ProductOrGroupAutocomplete from '../zones/ProductOrGroupAutocomplete';
import RecordImportDetails, { useRecordImportRows } from '../zones/RecordImportDetails';
import AddPlantingDialog from './AddPlantingDialog';
import { bandInputInvalid, effectiveRate, formatRate, isBanded } from '../../utils/bandRate';

interface Option {
  id: string;
  name: string;
}

export interface FeatureOption extends Option {
  category: string;
}

// From GET api/time-periods; dates are "yyyy-MM-dd".
interface SeasonOption extends Option {
  startDate: string;
  endDate: string;
}

interface ProductRow {
  // Exactly one is set — see RecordProductLine in BusinessMapTab.
  productId: string | null;
  productGroupId: string | null;
  productName: string;
  rate: string;
  rateUnitId: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
const toDatetimeLocal = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const toIso = (localDateTime: string): string | undefined => (localDateTime ? new Date(localDateTime).toISOString() : undefined);
const formatDay = (isoDate: string): string =>
  new Date(`${isoDate}T00:00:00Z`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

interface Props {
  open: boolean;
  businessId: string | null;
  // null: add a new record to businessId.
  recordId: string | null;
  // The business's farms/fields, for a new record's location.
  features?: FeatureOption[];
  onClose: () => void;
  onSaved: () => void;
}

// Add / edit one record outside the business map — the admin Records
// screen and Data Validation (double-click a record). Loads its own lookups
// and, when editing, the record itself (GET api/records/{id}). An imported
// record gets an "Imported" tab comparing it with the row(s) it came from.
// The map's own Edit Operation dialog (BusinessMapTab) adds shape drawing.
const RecordEditDialog: React.FC<Props> = ({ open, businessId, recordId, features = [], onClose, onSaved }) => {
  const auth = useAuth();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const [operationTypes, setOperationTypes] = useState<OperationType[]>([]);
  const [operationMethods, setOperationMethods] = useState<OperationMethod[]>([]);
  const [units, setUnits] = useState<Option[]>([]);
  const [geneStacks, setGeneStacks] = useState<Option[]>([]);
  const [regions, setRegions] = useState<Option[]>([]);
  const [seasons, setSeasons] = useState<SeasonOption[]>([]);

  const [record, setRecord] = useState<RecordItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<'record' | 'imported'>('record');
  const [locationId, setLocationId] = useState<string | null>(null);
  const [locationName, setLocationName] = useState('');
  const [typeId, setTypeId] = useState('');
  const [methodId, setMethodId] = useState('');
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');
  const [areaHectares, setAreaHectares] = useState('');
  const [bandPercent, setBandPercent] = useState('');
  const [geneStackId, setGeneStackId] = useState<string | null>(null);
  const [regionId, setRegionId] = useState<string | null>(null);
  const [timePeriodId, setTimePeriodId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { rows: importRows, loading: importLoading } = useRecordImportRows(open ? recordId : null, authHeaders);

  // Whether "Add planting record" applies: the record's field (or farm) has
  // no planting record in its season (GET api/records/{id}/planting).
  const [canAddPlanting, setCanAddPlanting] = useState(false);
  const [plantingOpen, setPlantingOpen] = useState(false);
  const [plantingAdded, setPlantingAdded] = useState<string | null>(null);
  const checkPlanting = useCallback(async () => {
    if (!recordId) return;
    const res = await fetch(`/api/records/${recordId}/planting`, { headers: authHeaders() }).catch(() => null);
    setCanAddPlanting(!!res?.ok && (await res.json()).canAdd);
  }, [recordId, authHeaders]);

  useEffect(() => {
    setCanAddPlanting(false);
    setPlantingAdded(null);
    if (open && recordId) checkPlanting();
  }, [open, recordId, checkPlanting]);

  useEffect(() => {
    if (!open || operationTypes.length > 0) return;
    (async () => {
      const [unitRes, opRes, methodRes, geneRes, regionRes, seasonRes] = await Promise.all([
        fetch('/api/units', { headers: authHeaders() }),
        fetch('/api/operation-types', { headers: authHeaders() }),
        fetch('/api/operation-methods', { headers: authHeaders() }),
        fetch('/api/gene-stacks', { headers: authHeaders() }),
        fetch('/api/regions', { headers: authHeaders() }),
        fetch('/api/time-periods', { headers: authHeaders() }),
      ]);
      if (seasonRes.ok) setSeasons((await seasonRes.json()).map((t: any) => ({ id: t.id, name: t.name, startDate: t.startDate, endDate: t.endDate })));
      if (regionRes.ok) setRegions((await regionRes.json()).filter((r: any) => !r.archivedDate).map((r: any) => ({ id: r.id, name: r.name })));
      if (unitRes.ok) setUnits((await unitRes.json()).map((u: any) => ({ id: u.id, name: u.name })));
      if (opRes.ok) setOperationTypes(await opRes.json());
      if (methodRes.ok) setOperationMethods(await methodRes.json());
      if (geneRes.ok) setGeneStacks((await geneRes.json()).filter((g: any) => !g.archivedDate).map((g: any) => ({ id: g.id, name: g.name })));
    })();
  }, [open, operationTypes.length, authHeaders]);

  // Fill the form: the record being edited, or blanks for a new one.
  useEffect(() => {
    if (!open) return;
    setTab('record');
    setError(null);
    setRecord(null);
    if (!recordId) {
      setLocationId(null);
      setLocationName('');
      setTypeId(operationTypes[0]?.id ?? '');
      setMethodId('');
      setTimeFrom(toDatetimeLocal(new Date()));
      setTimeTo('');
      setAreaHectares('');
      setBandPercent('');
      setGeneStackId(null);
      setRegionId(null);
      setTimePeriodId(null);
      setNotes('');
      setProducts([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`/api/records/${recordId}`, { headers: authHeaders() });
        if (!res.ok) throw new Error('Failed to load the record');
        const r: RecordItem & { geneStackId?: string | null } = await res.json();
        if (cancelled) return;
        setRecord(r);
        setLocationId(r.spatialFeatureId);
        setLocationName(r.locationName ?? '');
        setTypeId(r.operationTypeId);
        setMethodId(r.operationMethodId ?? '');
        setTimeFrom(toDatetimeLocal(new Date(r.timeFrom)));
        setTimeTo(r.timeTo ? toDatetimeLocal(new Date(r.timeTo)) : '');
        setAreaHectares(r.areaHectares != null ? String(r.areaHectares) : '');
        setBandPercent(r.bandPercent != null ? String(r.bandPercent) : '');
        setGeneStackId(r.geneStackId ?? null);
        setRegionId(r.regionId ?? null);
        setTimePeriodId(r.timePeriodId ?? null);
        setNotes(r.notes ?? '');
        setProducts(r.products.map((p) => ({
          productId: p.productId,
          productGroupId: p.productGroupId,
          productName: p.productName,
          rate: String(p.rate),
          rateUnitId: p.rateUnitId,
        })));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // operationTypes only seeds a new record's type, once loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recordId, authHeaders]);

  useEffect(() => {
    if (open && !recordId && !typeId && operationTypes.length > 0) setTypeId(operationTypes[0].id);
  }, [open, recordId, typeId, operationTypes]);

  const recordType = operationTypes.find((t) => t.id === typeId) ?? null;
  const methodOptions = recordType ? operationMethods.filter((m) => recordType.methodIds.includes(m.id)) : [];
  const areaInvalid = areaHectares.trim() !== '' && !(Number(areaHectares) >= 0);
  const bandInvalid = bandInputInvalid(bandPercent);

  // Same test as the record-outside-season validation rule: the record's
  // UTC date against the season's start/end. A hint only — saving is allowed.
  const season = seasons.find((s) => s.id === timePeriodId) ?? null;
  const recordDay = timeFrom ? toIso(timeFrom)?.slice(0, 10) : undefined;
  let seasonHint: string | undefined;
  if (season && recordDay && (recordDay < season.startDate || recordDay > season.endDate)) {
    const before = recordDay < season.startDate;
    const fits = seasons.find((s) => s.id !== season.id && recordDay >= s.startDate && recordDay <= s.endDate);
    seasonHint = `"When" is ${before ? 'before this season starts' : 'after this season ends'} (${formatDay(before ? season.startDate : season.endDate)})`
      + (fits ? ` — it falls in ${fits.name}` : '');
  }

  const updateProduct = (index: number, patch: Partial<ProductRow>) =>
    setProducts((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)));

  const handleSave = async () => {
    if (!businessId || !typeId || !timeFrom) return;
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        operationTypeId: typeId,
        operationMethodId: methodId || null,
        timeFrom: toIso(timeFrom),
        timeTo: timeTo ? toIso(timeTo) : null,
        notes: notes.trim() || null,
        geneStackId,
        areaHectares: areaHectares.trim() === '' ? null : Number(areaHectares),
        bandPercent: bandPercent.trim() === '' ? null : Number(bandPercent),
        products: products
          .filter((p) => (p.productId || p.productGroupId) && p.rateUnitId)
          .map((p) => ({ productId: p.productId, productGroupId: p.productGroupId, rate: Number(p.rate) || 0, rateUnitId: p.rateUnitId })),
      };
      if (recordId) {
        if (!methodId) body.clearOperationMethod = true;
        if (!geneStackId) body.clearGeneStack = true;
        // Only a changed region is sent: the record's own source text
        // (RegionName) stays as the file had it.
        if (regionId !== (record?.regionId ?? null)) {
          if (regionId) body.regionId = regionId; else body.clearRegion = true;
        }
        // Only a changed season is sent, so an archived one (not in the
        // list, so shown blank) isn't cleared by saving something else.
        if (timePeriodId !== (record?.timePeriodId ?? null)) {
          if (timePeriodId) body.timePeriodId = timePeriodId; else body.clearTimePeriod = true;
        }
        if (areaHectares.trim() === '') body.clearAreaHectares = true;
        if (bandPercent.trim() === '') body.clearBandPercent = true;
        if (locationId) {
          body.spatialFeatureId = locationId;
        } else if (locationName.trim()) {
          body.locationName = locationName.trim();
        }
      } else {
        body.spatialFeatureId = locationId;
        body.locationName = locationId ? null : locationName.trim() || null;
        // Blank: the server inherits the farm/field's region.
        if (regionId) body.regionId = regionId;
        if (timePeriodId) body.timePeriodId = timePeriodId;
      }

      const res = await fetch(recordId ? `/api/records/${recordId}` : `/api/businesses/${businessId}/records`, {
        method: recordId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        const fieldErrors = errorData.errors ? Object.values(errorData.errors).flat() : [];
        throw new Error(errorData.error || (fieldErrors as string[]).join(' ') || 'Failed to save record');
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const location = record
    ? [record.farmName, record.fieldName].filter(Boolean).join(' / ') || record.spatialFeatureName || record.locationName
    : null;

  // The record's farm and field in a new tab (pages/FieldView): its field
  // seasons and the other records there, in the season picked here.
  let fieldViewUrl: string | null = null;
  if (record && (record.farmId || record.fieldId)) {
    const query = new URLSearchParams({ businessId: record.businessId, recordId: record.id });
    if (record.farmId) query.set('farmId', record.farmId);
    if (record.fieldId) query.set('fieldId', record.fieldId);
    if (timePeriodId) query.set('timePeriodId', timePeriodId);
    fieldViewUrl = `/field-view?${query}`;
  }

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h6" component="h2">{recordId ? 'Edit Record' : 'Add Record'}</Typography>
          {location && <Typography variant="body2" color="text.secondary">{location}</Typography>}
        </Box>
        <Box display="flex" flexWrap="wrap" justifyContent="flex-end" gap={0.5} sx={{ flexShrink: 0, mt: 0.5 }}>
          {canAddPlanting && (
            <Button
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => setPlantingOpen(true)}
              title={`This field has no planting record in ${record?.timePeriodName ?? 'this season'}`}
            >
              Add planting record
            </Button>
          )}
          {fieldViewUrl && (
            <Button
              size="small"
              href={fieldViewUrl}
              target="_blank"
              rel="noopener"
              endIcon={<OpenInNewIcon fontSize="small" />}
              title="This farm and field's field seasons and other records, in a new tab"
            >
              Field view
            </Button>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {recordId && importRows.length > 0 && (
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tab value="record" label="Record" />
            <Tab value="imported" label={`Imported${importRows.length > 1 ? ` (${importRows.length} rows)` : ''}`} />
          </Tabs>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {plantingAdded && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPlantingAdded(null)}>
            Planting record added: {plantingAdded}.
          </Alert>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
        ) : tab === 'imported' && importRows.length > 0 ? (
          <Box mt={1}>
            <RecordImportDetails rows={importRows} loading={importLoading} record={record} />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>Operation Type</InputLabel>
              <Select label="Operation Type" value={typeId} onChange={(e) => { setTypeId(e.target.value); setMethodId(''); }}>
                {operationTypes.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={methodOptions.length === 0}>
              <InputLabel>Method (optional)</InputLabel>
              <Select label="Method (optional)" value={methodId} onChange={(e) => setMethodId(e.target.value)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {methodOptions.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
              </Select>
            </FormControl>

            {!recordId && (
              <Autocomplete
                options={features}
                getOptionLabel={(o) => `${o.name}${o.category ? ` (${o.category})` : ''}`}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={features.find((f) => f.id === locationId) || null}
                onChange={(_, value) => setLocationId(value?.id || null)}
                renderInput={(params) => <TextField {...params} label="Location (Farm/Field, optional)" />}
              />
            )}
            {!locationId && (
              <TextField fullWidth label="Location Name (optional, free text)" value={locationName} onChange={(e) => setLocationName(e.target.value)} />
            )}

            <Box display="flex" gap={2}>
              <TextField fullWidth type="datetime-local" label="When" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField fullWidth type="datetime-local" label="To (optional)" value={timeTo} onChange={(e) => setTimeTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Box>

            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                type="number"
                label="Area (ha)"
                value={areaHectares}
                onChange={(e) => setAreaHectares(e.target.value)}
                error={areaInvalid}
                helperText={
                  areaInvalid
                    ? 'Must be 0 or more'
                    : record?.mappedAreaHectares != null
                      ? `Blank uses the mapped area (${record.mappedAreaHectares.toLocaleString('en-AU', { maximumFractionDigits: 2 })} ha)`
                      : 'Blank uses the mapped area, when there is one'
                }
              />
              <TextField
                fullWidth
                type="number"
                label="Band % (optional)"
                value={bandPercent}
                onChange={(e) => setBandPercent(e.target.value)}
                error={bandInvalid}
                helperText={bandInvalid ? 'Must be above 0 and at most 100' : 'Share of the area treated. Blank is all of it'}
              />
              <Autocomplete
                fullWidth
                options={geneStacks}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={geneStacks.find((g) => g.id === geneStackId) || null}
                onChange={(_, value) => setGeneStackId(value?.id || null)}
                renderInput={(params) => <TextField {...params} label="Gene stack (optional)" />}
              />
            </Box>

            <Box display="flex" gap={2}>
              <Autocomplete
                fullWidth
                options={regions}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={regions.find((r) => r.id === regionId) || null}
                onChange={(_, value) => setRegionId(value?.id || null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Region"
                    helperText={
                      record && !record.regionId && record.regionName
                        ? `Not linked — the source said "${record.regionName}"`
                        : !recordId ? 'Blank uses the farm/field\'s region' : undefined
                    }
                  />
                )}
              />
              <Autocomplete
                fullWidth
                options={seasons}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={season}
                onChange={(_, value) => setTimePeriodId(value?.id || null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Season (optional)"
                    helperText={seasonHint}
                    FormHelperTextProps={{ sx: { color: 'warning.main' } }}
                  />
                )}
              />
            </Box>

            <TextField fullWidth multiline minRows={2} label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />

            <Divider />
            <Typography variant="subtitle2">Products applied</Typography>
            {products.map((row, i) => (
              <Box key={i} display="flex" gap={1} alignItems="center">
                <ProductOrGroupAutocomplete
                  authHeader={authHeaders()}
                  productId={row.productId}
                  productGroupId={row.productGroupId}
                  name={row.productName}
                  onChange={(selection) =>
                    updateProduct(i, {
                      productId: selection?.kind === 'product' ? selection.id : null,
                      productGroupId: selection?.kind === 'group' ? selection.id : null,
                      productName: selection?.name ?? '',
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Application rate"
                  type="number"
                  sx={{ width: 130 }}
                  value={row.rate}
                  onChange={(e) => updateProduct(i, { rate: e.target.value })}
                  helperText={isBanded(Number(bandPercent)) && Number(row.rate) > 0 ? `${formatRate(effectiveRate(Number(row.rate), Number(bandPercent)))} effective` : undefined}
                />
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <InputLabel>Unit</InputLabel>
                  <Select label="Unit" value={row.rateUnitId} onChange={(e) => updateProduct(i, { rateUnitId: e.target.value })}>
                    {units.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <IconButton size="small" onClick={() => setProducts((prev) => prev.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setProducts((prev) => [...prev, { productId: null, productGroupId: null, productName: '', rate: '', rateUnitId: '' }])}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add product
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" disabled={saving || loading || !typeId || !timeFrom || areaInvalid || bandInvalid} onClick={handleSave}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
      <AddPlantingDialog
        open={plantingOpen}
        recordId={recordId}
        authHeaders={authHeaders}
        onClose={() => setPlantingOpen(false)}
        onAdded={(summary) => {
          setPlantingAdded(summary);
          checkPlanting();
          // The list behind (e.g. Validation's "Defoliation with no planting") changes.
          onSaved();
        }}
      />
    </Dialog>
  );
};

export default RecordEditDialog;
